import { DataSelectionBinding } from '../../../models/data-selection';
import { WorkspaceNavigationService } from '../../../services/workspace-navigation.service';
import { Optional } from '@angular/core';
import { MapNavigationService } from '../../../map/core/map-navigation.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { auditTime, combineLatest, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, toArray } from 'rxjs/operators';
import { DataTableColumn } from '../../../shared/ui/data-table/data-table.component';
import { MapSelectionContextService, FeatureSelectionSummary } from '../../../map/core/map-selection-context.service';
import { PlaceFeatureBase } from '../../../map/core/base/place-feature.base';
import { MapObjectId } from '../../../map/core/map-object';
import { DataFetch } from '../../../models/data-source';
import { Territory } from '../../../map/features/territories/territory-feature';
import { ZipCodeAreaDetails } from '../../../map/features/zipcode-areas/zipcode-area-feature';
import { LeadsBoardStateService } from '../../leads/board/leads-board-state.service';
import { SELECTION_WORKSPACE_ROUTE } from '../../../services/workspace-route';

interface SelectionRow {
  id: MapObjectId;
  name: string;
  description: string;
  path?: string;
}

/** Live business-object selections rendered inside the ordinary Workspace shell. */
@Component({
  selector: 'app-selection-workspace',
  providers: [LeadsBoardStateService],
  templateUrl: './selection-workspace.component.html',
  styleUrls: ['./selection-workspace.component.css'],
})
export class SelectionWorkspaceComponent implements OnInit, OnDestroy {
  readonly routePath = SELECTION_WORKSPACE_ROUTE;
  readonly detail$ = combineLatest([this.selectionContext.selections$, this.navigation.detail$]).pipe(
    auditTime(0),
    map(([groups, detail]) => {
      const single = groups.length === 1 && groups[0].count === 1 && groups[0].objectIds.length === 1;
      return single && this.navigation.hasObjectFocus(groups[0].feature.key, groups[0].objectIds[0]) ? detail : null;
    }),
  );

  format(value: unknown): string {
    return typeof value === 'boolean' ? value ? 'Yes' : 'No'
      : typeof value === 'number' ? value.toLocaleString() : String(value ?? '');
  }
  selections: readonly FeatureSelectionSummary[] = [];
  active: FeatureSelectionSummary | undefined;
  selectedRecordIds: readonly number[] = [];
  selectionBinding: DataSelectionBinding | null = null;
  loading = false;
  error = '';
  fetch: DataFetch<SelectionRow> | null = null;
  readonly columns: DataTableColumn[] = [
    { id: 'id', label: 'ID' },
    { id: 'name', label: 'Name', sortable: false },
    { id: 'description', label: 'Details', sortable: false },
  ];
  private readonly subscriptions = new Subscription();
  private readonly changed = new Subject<void>();
  private resolution?: Subscription;
  private activeSelection?: Subscription;
  filters = {};

  constructor(
    public readonly selectionContext: MapSelectionContextService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly navigation: MapNavigationService,
    @Optional() private readonly contextNavigation?: WorkspaceNavigationService,
  ) {}

  ngOnInit(): void {
    const view$ = combineLatest([
      this.selectionContext.selections$, this.route.queryParamMap, this.contextNavigation?.viewReady$ ?? of(true),
    ]);
    this.subscriptions.add(view$.subscribe(([selections, params]) => {
      this.selections = selections;
      const key = params.get('feature');
      const active = selections.find(item => item.feature.key === key)
        ?? selections.find(item => item.feature.key === this.active?.feature.key)
        ?? selections[0];
      const replaced = active?.feature !== this.active?.feature;
      this.active = active;
      if (replaced) {
        const feature = active?.feature;
        this.selectionBinding = feature?.isObjectSelected && feature.setObjectsSelected ? {
          key: `map-object-selection:${feature.key}`,
          changed$: feature.selectionCount$,
          has: id => (typeof id === 'string' || typeof id === 'number') && feature.isObjectSelected!(id),
          setSelected: (ids, selected) => feature.setObjectsSelected!(
            ids.filter((id): id is string | number => typeof id === 'string' || typeof id === 'number'), selected),
        } : null;
        this.activeSelection?.unsubscribe();
        this.activeSelection = active?.feature.selectionCount$.subscribe(() => this.load());
        if (!active) this.load();
      }
    }));
    // Normalize after synchronous owner/router updates settle. A session reset can
    // clear several owners during navigation; redirecting inside that event would
    // re-enter the router before its current transition has finished.
    this.subscriptions.add(view$.pipe(auditTime(0)).subscribe(([, params, viewReady]) => {
      const key = this.active?.feature.key ?? null;
      if (viewReady && key !== params.get('feature')) {
        void this.router.navigate([], {
          relativeTo: this.route, queryParams: { feature: key },
          replaceUrl: true,
        });
      }
    }));
  }

  openFeature(feature: string): void {
    void this.router.navigate([this.routePath], { queryParams: { feature } });
  }

  ngOnDestroy(): void {
    this.changed.next();
    this.changed.complete();
    this.resolution?.unsubscribe();
    this.activeSelection?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  load(): void {
    this.changed.next();
    this.resolution?.unsubscribe();
    this.fetch = null;
    this.selectedRecordIds = [];
    this.error = '';
    this.loading = !!this.active;
    const feature = this.active?.feature;
    if (!feature) return;
    // Keep direct IDs even when navigation restores an off-screen object without
    // loaded geometry. Point features expand only deferred members to business IDs.
    const ids$ = feature instanceof PlaceFeatureBase
      ? feature.resolveSelectedObjects().pipe(map(objects => [...feature.selectedObjectIds, ...objects.map(object => object.id)]))
      : of(feature.selectedObjectIds);
    this.resolution = ids$.subscribe({
      next: selectedIds => {
        const ids = [...new Set(selectedIds)].sort((a, b) =>
          typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b)));
        if (feature.key === 'customers' || feature.key === 'leads') {
          this.selectedRecordIds = ids.filter((id): id is number => typeof id === 'number');
          this.loading = false;
          return;
        }
        this.fetch = query => {
          const filtered = ids.filter(id => String(id).includes(query.search.trim()));
          const ordered = query.desc ? [...filtered].reverse() : filtered;
          const page = ordered.slice((query.page - 1) * query.pageSize, query.page * query.pageSize);
          // Read only the visible page, with bounded concurrency and cancellation.
          return from(page).pipe(
            mergeMap((id, index) => this.readRow(feature.key, id).pipe(map(row => ({ row, index }))), 4),
            toArray(),
            map(rows => ({ items: rows.sort((a, b) => a.index - b.index).map(item => item.row), total: filtered.length })),
            takeUntil(this.changed),
          );
        };
        this.filters = {};
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Could not load the selection. Try again.';
      },
    });
  }

  private readRow(key: string, id: MapObjectId): Observable<SelectionRow> {
    const encoded = encodeURIComponent(String(id));
    let row: Observable<SelectionRow>;
    switch (key) {
      case 'territories':
        row = this.http.get<Territory>(`/api/territories/${encoded}`).pipe(map(territory => ({
          id: territory.id, name: territory.name, description: territory.status,
          path: '/leads/territories',
        })));
        break;
      case 'zipcode-areas':
        row = this.http.get<ZipCodeAreaDetails>(`/api/zipcode-areas/${encoded}`).pipe(map(zip => ({
          id: zip.zipCode, name: `ZIP ${zip.zipCode}`,
          description: zip.counties.map(county => `${county.name}, ${county.stateName}`).join('; '),
        })));
        break;
      case 'prospect':
        row = of({ id, name: `Cell ${id}`, description: 'Lead Discovery', path: '/prospect' });
        break;
      default:
        row = of({ id, name: String(id), description: this.active?.feature.name ?? '' });
    }
    return row.pipe(catchError((error: HttpErrorResponse) => {
      if (error.status !== 404) throw error;
      return of({ id, name: 'Unavailable', description: 'This object is no longer available.' });
    }));
  }
}
